import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Divider, Typography } from "antd";
import { useSelector } from "react-redux";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartLoader from "./ChartLoader";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const { Title: AntTitle } = Typography;

const ApproximateChargesChart = () => {
  const { whatsappBusinessInsightsLoading, WhatsappBusinessCreditUsageInsights } =
    useSelector((store) => store.Channel);

  const chartData = useMemo(() => {
    if (!WhatsappBusinessCreditUsageInsights?.length) return null;

    // Group data by date and conversation category for costs
    const groupedData = WhatsappBusinessCreditUsageInsights.reduce((acc, item) => {
      const date = new Date(item.usage_date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

      if (!acc[date]) {
        acc[date] = {
          marketing: 0,
          utility: 0,
          authentication: 0,
          service: 0,
          total: 0,
        };
      }

      // Update each category amount
      acc[date].marketing = parseFloat(item.total_marketing_credits) || 0;
      acc[date].utility = parseFloat(item.total_utility_credits) || 0;
      acc[date].authentication = parseFloat(item.total_authentication_credits) || 0;
      acc[date].service = parseFloat(item.total_service_credits) || 0;

      // Calculate total for the date
      acc[date].total = acc[date].marketing + acc[date].utility +
                        acc[date].authentication + acc[date].service;

      return acc;
    }, {});

    // Sort dates
    const sortedDates = Object.keys(groupedData).sort((a, b) => {
      return new Date(a) - new Date(b);
    });

    return {
      labels: ["", ...sortedDates],
      datasets: [
        {
          label: "Marketing",
          data: [null, ...sortedDates.map((date) => groupedData[date].marketing)],
          borderColor: "#9748ff",
          borderWidth: 1.5,
          backgroundColor: "#9748ff",
          tension: 0.4,
        },
        {
          label: "Utility",
          data: [null, ...sortedDates.map((date) => groupedData[date].utility)],
          borderColor: "#f6a722",
          borderWidth: 1.5,
          backgroundColor: "#f6a722",
          tension: 0.4,
        },
        {
          label: "Authentication",
          data: [null, ...sortedDates.map((date) => groupedData[date].authentication)],
          borderColor: "#1578f2",
          borderWidth: 1.5,
          backgroundColor: "#1578f2",
          tension: 0.4,
        },
        {
          label: "Service",
          data: [null, ...sortedDates.map((date) => groupedData[date].service)],
          borderColor: "#00d998",
          backgroundColor: "#00d998",
          borderWidth: 1.5,
          tension: 0.4,
        },
      ],
      // Store totals for tooltip
      totals: [null, ...sortedDates.map((date) => groupedData[date].total)],
    };
  }, [WhatsappBusinessCreditUsageInsights]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `₹${value.toFixed(2)}`,
          stepSize: 1,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        position: "nearest",
        callbacks: {
          title: (tooltipItems) => {
            return `Date: ${tooltipItems[0].label}`;
          },
          label: (tooltipItem) => {
            return `${tooltipItem.dataset.label}: ₹${tooltipItem.raw.toFixed(
              2,
            )}`;
          },
          footer: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const total = chartData?.totals[index];
            return `Total Charges: ₹${total?.toFixed(2)}`;
          },
        },
      },
    },
  };

  const EMPTY_CHART_DATA = {
    labels: [],
    datasets: [
      {
        label: "Marketing",
        data: [],
        borderColor: "#9748ff",
        borderWidth: 1.5,
        tension: 0.4,
      },
      {
        label: "Utility",
        data: [],
        borderColor: "#f6a722",
        borderWidth: 1.5,
        tension: 0.4,
      },
      {
        label: "Authentication",
        data: [],
        borderColor: "#1578f2",
        borderWidth: 1.5,
        tension: 0.4,
      },
      {
        label: "Service",
        data: [],
        borderColor: "#00d998",
        borderWidth: 1.5,
        tension: 0.4,
      },
    ],
  };

  return (
    <div className="w-full p-4 bg-white rounded-lg">
      {/* <AntTitle level={5} className="text-gray-700 mb-4">
        Approximate Charges
      </AntTitle> */}
      {/* <Divider className="mt-0" /> */}
      <div className="w-[60vw] mx-auto h-96 relative">
        {whatsappBusinessInsightsLoading ? (
          <ChartLoader />
        ) : (
          <>
            <Line data={chartData || EMPTY_CHART_DATA} options={options} />
            {(!chartData?.labels || chartData.labels.length === 0) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white bg-opacity-80">
                <span className="text-gray-500 text-lg font-medium">
                  No data available
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApproximateChargesChart;
